import cn from 'classnames';
import React, { useRef, useState } from 'react';

import SvgCheck from '@sats-group/icons/16/check';
import SvgArrowUp from '@sats-group/icons/24/arrow-up';
import SvgClose from '@sats-group/icons/24/close';
import Button from '@sats-group/ui-lib/react/button/button';
import Collapse from '@sats-group/ui-lib/react/collapse/collapse';
import useClickOutside from '@sats-group/ui-lib/react/hooks/use-click-outside';
import LinkButton from '@sats-group/ui-lib/react/link-button';
import Text from '@sats-group/ui-lib/react/text';

import { registerFormId } from 'ui/pages/register/register.page';

import useOnPageShow from 'utils/use-on-page-show';

import type { CheckoutSummary as Props } from './checkout-summary.props';

const CheckoutSummary: React.FunctionComponent<Props> = ({
  activeCampaign,
  activeCorporateAgreement,
  benefits,
  buttonLabel,
  description,
  link,
  priceItems,
  selectedItems,
  sponsorshipType,
  standardPrice,
  submit,
  title,
  totalPrice,
}) => {
  const [isOpen, toggleIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const checkoutSummary = cn('checkout-summary', {
    'checkout-summary--opened': isOpen,
  });

  useClickOutside(ref, () => toggleIsOpen(false));
  useOnPageShow(setIsSubmitting);

  // NOTE: Not moving to native html form submit until we move away from split layout, as passing the loading state to the button is iffy.
  const handleSubmit = () => {
    setIsSubmitting(true);
    const form = document.getElementById(registerFormId) as HTMLFormElement;
    if (form) {
      if (form.checkValidity()) {
        form.submit();
      } else {
        form.reportValidity();
        setIsSubmitting(false);
      }
    }
  };

  return (
    <>
      <div className={checkoutSummary} ref={ref}>
        <Collapse
          isOpen={isOpen}
          className="checkout-summary__collapse"
          duration={200}
        >
          <div>
            <div className="checkout-summary__header">
              <Text theme={Text.themes.emphasis}>{title}</Text>
              <Text size={Text.sizes.small}>{description}</Text>
            </div>

            <div className="checkout-summary__body">
              {benefits?.length ? (
                <div className="checkout-summary__section">
                  {benefits.map(benefit => (
                    <div key={benefit} className="checkout-summary__benefit">
                      <div className="checkout-summary__benefit-icon">
                        <SvgCheck />
                      </div>
                      <Text size={Text.sizes.small}>{benefit}</Text>
                    </div>
                  ))}
                </div>
              ) : null}

              {activeCampaign ? (
                <div className="checkout-summary__section">
                  <div className="checkout-summary__line-item">
                    <Text theme={Text.themes.emphasis} size={Text.sizes.small}>
                      {activeCampaign.title}
                    </Text>
                    <Text size={Text.sizes.small}>{activeCampaign.value}</Text>
                  </div>
                </div>
              ) : null}

              {activeCorporateAgreement ? (
                <div className="checkout-summary__section">
                  <div className="checkout-summary__line-item">
                    <Text theme={Text.themes.emphasis} size={Text.sizes.small}>
                      {activeCorporateAgreement.title}
                    </Text>
                    <div className="checkout-summary__line-item-value">
                      <Text size={Text.sizes.small}>
                        {activeCorporateAgreement.value}
                      </Text>
                      {activeCorporateAgreement.href ? (
                        <a
                          href={activeCorporateAgreement.href}
                          aria-label={activeCorporateAgreement.label}
                          title={activeCorporateAgreement.label}
                        >
                          <SvgClose />
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              {selectedItems.length ? (
                <div className="checkout-summary__section">
                  {selectedItems.map(({ title, value }) => (
                    <div key={title} className="checkout-summary__line-item">
                      <Text
                        theme={Text.themes.emphasis}
                        size={Text.sizes.small}
                      >
                        {title}
                      </Text>
                      <Text size={Text.sizes.small}>{value}</Text>
                    </div>
                  ))}
                </div>
              ) : null}

              {priceItems.length ? (
                <div className="checkout-summary__section">
                  {priceItems.map(({ title, value }) => (
                    <div key={title} className="checkout-summary__line-item">
                      <Text
                        theme={Text.themes.emphasis}
                        size={Text.sizes.small}
                      >
                        {title}
                      </Text>
                      <Text size={Text.sizes.small}>{value}</Text>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </Collapse>

        <div className="checkout-summary__footer">
          <div className="checkout-summary__mobile-toggle">
            <button
              aria-label={isOpen ? buttonLabel.close : buttonLabel.open}
              aria-expanded={isOpen}
              className="checkout-summary__button"
              onClick={event => {
                event.stopPropagation();
                toggleIsOpen(!isOpen);
              }}
            >
              <div
                className={cn('checkout-summary__toggle-arrow', {
                  'checkout-summary__toggle-arrow--flipped': isOpen,
                })}
              >
                <SvgArrowUp />
              </div>
              <div className="checkout-summary__price-mobile-wrapper">
                {standardPrice ? (
                  <Text
                    className="checkout-summary__standard-price"
                    size={Text.sizes.small}
                  >
                    {standardPrice}
                  </Text>
                ) : null}
                <div className="checkout-summary__total-price-mobile">
                  <Text>{totalPrice.title}</Text>
                  <Text theme={Text.themes.emphasis}>{totalPrice.value}</Text>
                </div>
              </div>
            </button>
            {link ? (
              <div className="checkout-summary__next">
                <LinkButton
                  {...link}
                  size={LinkButton.sizes.large}
                  data-test-next-summary
                />
              </div>
            ) : null}
            {submit ? (
              <div className="checkout-summary__next">
                <Button
                  data-test-next-summary
                  type="submit"
                  form="registration-form"
                  disabled={isSubmitting ? true : undefined}
                  spinning={isSubmitting}
                  {...submit}
                  size={Button.sizes.large}
                  onClick={handleSubmit}
                />
              </div>
            ) : null}
          </div>
          <div className="checkout-summary__price-desktop">
            <Text size={Text.sizes.small}>{totalPrice.title}</Text>
          </div>
          <div className="checkout-summary__price-desktop">
            <div className="checkout-summary__total-price">
              {standardPrice ? (
                <Text
                  className="checkout-summary__standard-price"
                  size={Text.sizes.small}
                >
                  {standardPrice}
                </Text>
              ) : null}
              <Text
                className="checkout-summary__first-price"
                size={Text.sizes.headline3}
                theme={Text.themes.emphasis}
              >
                {totalPrice.value}
              </Text>
            </div>
          </div>
        </div>
        {sponsorshipType ? (
          <div className="checkout-summary__sponsorship-type">
            <Text size={Text.sizes.small}>{sponsorshipType}</Text>
          </div>
        ) : null}
      </div>
      {isOpen ? <div className="checkout-summary__backdrop"></div> : null}
    </>
  );
};

export default CheckoutSummary;
